/* eslint-disable no-unused-vars */
import { ReactElement, Fragment } from 'react';
import { useTranslation } from 'react-i18next';
import { Row, Col } from 'react-bootstrap';
import moment from 'moment';
import { useSelector } from 'react-redux';
import { StateRedux } from '../../../store/type-redux';
import {
  Table, Search, Button, SplitButtonWithIcon, AlertReact, load, ButtonWithEventClick,
} from '../../../components';
import { Usuario, Pesquisa } from '../../../@types/exports';
import { api, alerts, doRefreshToken } from '../../../utils';

interface Props {
  usuarios: Usuario[];
  pesquisa: (x: Pesquisa) => void;
  setEditarItem: (u: Usuario | undefined) => void;
  removerItem: (u: Usuario) => void;
}

interface HtmlProps {
  data: {
    clientRole: boolean;
    composite: boolean;
    containerId: string;
    description: string;
    id: string;
    name: string;
  }[];
  realm: string;
}
const Texto = (textoToReplace: any) => {
  const { t } = useTranslation();
  return (
    t(`usuarios.${textoToReplace}`)
  );
};

const Html = ({ data, realm }: HtmlProps) => (
  <div>
    {data ? (
      <>
        <h4><b>{Texto('perfil')}</b></h4>
        <hr />
        {data.map((item: any) => (
          <Fragment key={`${item.name}-${item.description}`}>
            <h4 className="text-secondary" style={{ textAlign: 'left' }}>
              <b>{item.name}</b>
            </h4>
            <p className="card-text my-1 text-secondary" style={{ textAlign: 'left' }}>
              {item.description}
            </p>
            <hr />
          </Fragment>
        ))}
      </>
    ) : (
      <h4><b>{Texto('none')}</b></h4>
    )}
  </div>
);

const HtmlDeletPerfil = (texto: any) => {
  const { textoToReplace } = texto;
  return (
    <div>
      <h4><b>{Texto(textoToReplace)}</b></h4>
    </div>
  );
};

const HtmlBotao = (texto: any) => {
  const { textoToReplace } = texto;
  return (
    <div>
      {Texto(textoToReplace)}
    </div>
  );
};

const GridUsuarios = ({
  usuarios, pesquisa, setEditarItem, removerItem,
}: Props): ReactElement => {
  // Verifica se é necessário realizar o refresh token
  doRefreshToken.getRefreshToken();
  const { realm: { nameRealm } } = useSelector((state: StateRedux) => state);

  const realm = nameRealm;

  const { t } = useTranslation();

  const handleClickInfo = async (usuarioId: string) => {
    // Verifica se é necessário realizar o refresh token
    doRefreshToken.getRefreshToken();
    try {
      load.show();
      const { data } = await api.getPermissaoPorUsuario(usuarioId);
      AlertReact.message({
        html: <Html data={data.msg} realm={realm} />,
        confirmButtonColor: '#2e59d9',
      });
    } catch (error) {
      alerts.exibirErrorCatch(error);
    } finally {
      load.hide();
    }
  };

  const deletarPermissoes = async (usuarioId: string) => {
    // Verifica se é necessário realizar o refresh token
    doRefreshToken.getRefreshToken();
    AlertReact.message({
      html: <HtmlDeletPerfil textoToReplace="deletarTodasPermissioes" />,
      showDenyButton: true,
      denyButtonText: <HtmlBotao textoToReplace="botoes.voltar" />,
    })
      .then(async (result) => {
        try {
          load.show();
          if (result.isConfirmed) {
            await api.removerPermissoesUsuario(usuarioId);
            alerts.exibirToastSuccess(t('usuarios.alertaPerfisDeletados'));
          }
        } catch (error) {
          alerts.exibirErrorCatch(error);
        } finally {
          load.hide();
        }
      });
  };

  return (
    <div className="card shadow mb-4">
      <div className="card-body">
        <div className="table-responsive" style={{ overflowX: 'hidden' }}>
          <div className="dataTables_wrapper dt-bootstrap4">
            <Search
              show={Texto('show')}
              todos={Texto('todos')}
              searchTag={Texto('searchTag')}
              pesquisa={pesquisa}
              placeholderSearch={Texto('nome')}
              componentRight={(
                <SplitButtonWithIcon
                  label={Texto('novoUsuario')}
                  dataTarget="modal-cadastro-usuario"
                  icon="plus"
                  onClick={() => setEditarItem(undefined)}
                />
              )}
            />
            <br />
            <Row>
              <Col sm={12}>
                <Table
                  empty={!usuarios.length}
                  th={[
                    Texto('nome'),
                    'Username',
                    'Email',
                    Texto('emailAtivo'),
                    Texto('perfil'),
                    Texto('ultimoLogin'),
                    Texto('ativo'),
                    Texto('opcoes'),
                  ]}
                  td={usuarios.map((usuario) => (
                    <tr key={usuario.id}>
                      <td>{`${usuario.firstName} ${usuario.lastName}`}</td>
                      <td>{usuario.username}</td>
                      <td>{usuario.email}</td>
                      <td>{usuario.emailVerified ? 'Sim' : 'Não'}</td>
                      {/* permissoes */}
                      <td>
                        <ButtonWithEventClick
                          nameClass1="btn btn-secondary btn-circle btn-sm"
                          nameClass2="fas fa-info"
                          handleClick={handleClickInfo}
                          parameterHandle={usuario.id}
                        />
                        &nbsp;&nbsp;

                        <ButtonWithEventClick
                          nameClass1="btn btn-danger btn-circle btn-sm"
                          nameClass2="fas fa-trash"
                          handleClick={deletarPermissoes}
                          parameterHandle={usuario.id}
                        />
                      </td>
                      <td>{moment(usuario.createdTimestamp).format('lll')}</td>
                      <td>
                        <span className="m-badge m-badge--wide m-badge--info">
                          {usuario.enabled ? 'Sim' : 'Não'}
                        </span>
                      </td>
                      <td>
                        <Button
                          className="btn btn-info btn-circle btn-sm"
                          data-toggle="modal"
                          data-target="#modal-cadastro-usuario"
                          onClick={() => setEditarItem(usuario)}
                        >
                          <i className="fas fa-pen" />
                        </Button>

                        &nbsp;&nbsp;

                        <ButtonWithEventClick
                          nameClass1="btn btn-danger btn-circle btn-sm"
                          nameClass2="fas fa-trash"
                          handleClick={removerItem}
                          parameterHandle={usuario}
                        />
                      </td>
                    </tr>
                  ))}
                />
              </Col>
            </Row>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GridUsuarios;
